

import { Building2, Play } from 'lucide-react';
import type { Product } from './types';
import { normalizeProductPricing } from './pricingNormalization';

export const coworkingManagementSuite: Product = normalizeProductPricing({
  id: 'coworking-management-suite',
  title: 'Coworking Space Management Suite',
  subtitle: 'Complete coworking & flexible workspace operations platform',
  description: 'Comprehensive coworking space management system featuring member management, space booking, access control, billing automation, community tools, and analytics for modern flexible workspace operations.',
  icon: Building2,
  image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  category: 'Facilities Management',
  status: 'Live',
  features: [
    // Member Management
    'Member Registration & Onboarding',
    'Member Profile & Portfolio Management',
    'Membership Plans & Tier Management',
    'Member Directory & Networking Tools',
    'Member Check-in/Check-out Tracking',
    'Member Communication & Messaging',

    // Space & Resource Booking
    'Real-Time Space Availability & Booking',
    'Meeting Room & Conference Space Reservations',
    'Hot Desk & Dedicated Desk Management',
    'Equipment & Resource Booking System',
    'Event Space & Venue Management',
    'Booking Calendar & Schedule Integration',

    // Access Control & Security
    'Smart Card & Key Fob Access Control',
    'Mobile App Door Access & QR Codes',
    'Security Camera Integration & Monitoring',
    'Visitor Management & Guest Registration',
    'After-Hours Access & Security Protocols',
    'Emergency Contact & Safety Systems',

    // Billing & Payment Management
    'Automated Billing & Invoice Generation',
    'Multiple Payment Gateway Integration',
    'Membership Fee & Usage-Based Billing',
    'Credit System & Wallet Management',
    'Expense Tracking & Financial Reporting',
    'Late Payment & Collection Management',

    // Community & Networking
    'Member Community Platform & Forums',
    'Event Management & RSVP System',
    'Skill Sharing & Collaboration Tools',
    'Business Directory & Member Showcases',
    'Internal Job Board & Opportunities',
    'Social Feed & Announcement System',

    // Operations Management
    'Staff Management & Role Assignment',
    'Maintenance Request & Ticket System',
    'Inventory Management & Supply Tracking',
    'Cleaning Schedule & Facility Management',
    'Vendor Management & Service Coordination',
    'Incident Reporting & Resolution',

    // Analytics & Reporting
    'Space Utilization & Occupancy Analytics',
    'Revenue Reporting & Financial Dashboards',
    'Member Engagement & Retention Metrics',
    'Booking Patterns & Demand Forecasting',
    'Operational Efficiency Reports',
    'Custom KPI Tracking & Insights',

    // Mobile Applications
    'Member Mobile App for iOS & Android',
    'Space Booking & Real-Time Availability',
    'Digital Access & Check-in Features',
    'Community Interaction & Messaging',
    'Payment & Account Management',
    'Push Notifications & Alerts',

    // Integration Capabilities
    'Accounting Software Integration (QuickBooks, Xero)',
    'Calendar Integration (Google, Outlook)',
    'CRM System Integration & Lead Management',
    'Email Marketing Platform Connections',
    'Property Management System Integration',
    'IoT Device & Smart Building Integration',

    // Marketing & Growth Tools
    'Lead Generation & Conversion Tracking',
    'Referral Program & Incentive Management',
    'Marketing Campaign & Email Automation',
    'Social Media Integration & Promotion',
    'Website Integration & Online Booking',
    'SEO Tools & Local Business Optimisation',

    // Communication Tools
    'Multi-Channel Communication System',
    'SMS & WhatsApp Integration',
    'Email Templates & Automated Messaging',
    'Member Newsletter & Update Distribution',
    'Feedback Collection & Survey Tools',
    'Live Chat & Support System',

    // Compliance & Legal
    'Lease Agreement & Contract Management',
    'Insurance & Liability Documentation',
    'Data Privacy & GDPR Compliance',
    'Financial Audit & Record Keeping',
    'Health & Safety Compliance Tracking',
    'Legal Document Storage & Management',
  ],
  pricing: { setup: 30720, monthly: 550 },
  color: 'from-purple-600 to-indigo-700',
  businessTypes: ['#CoworkingSpace', '#FlexibleWorkspace', '#SharedOffice', '#StartupHub', '#BusinessCenter'],
  demoConfig: {
    type: 'interactive',
    url: '/products/coworking-management-suite/demo',
    title: 'Experience the Coworking Platform',
    description: 'Explore our coworking management suite with live booking, member management and analytics',
    buttonText: 'Launch Coworking Demo',
    icon: Play,
    buttonColor: 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700',
  },
});

